import { ArrowUpRight, Mail, Instagram, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { NAV_ITEMS, PORTAL_URL } from "@/lib/data";
import { LEGAL_SLUGS } from "@/lib/legalContent";
import { useRegisterModal } from "@/context/RegisterModalContext";
import { useLanguage } from "@/context/LanguageContext";
import { FadeUp } from "./AnimatedSection";

const SOCIAL_LINKS = [
  { key: "instagram", href: "https://www.instagram.com/doble_r_records", icon: Instagram },
  { key: "youtube", href: "https://www.youtube.com/c/doblerrecordsdrr", icon: Youtube },
];

export const Footer = () => {
  const { openModal } = useRegisterModal();
  const { t } = useLanguage();

  return (
    <footer id="contacto" data-testid="footer-section" className="relative bg-black border-t border-white/[0.06] pt-24 md:pt-32 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FadeUp className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 pb-16 border-b border-white/[0.06]">
          <h2 className="font-heading font-bold text-white text-4xl md:text-5xl tracking-tight max-w-xl">
            {t.footer.ctaHeading}
          </h2>
          <div className="flex flex-wrap gap-4 shrink-0">
            <button
              data-testid="footer-register-button"
              onClick={openModal}
              className="group bg-brand-red text-white text-sm font-bold px-6 py-3.5 rounded-full flex items-center gap-2 hover:-translate-y-0.5 hover:bg-brand-redDark shadow-[0_8px_30px_-6px_rgba(255,36,54,0.5)] transition-all duration-300"
            >
              {t.nav.register}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-login-button"
              className="text-sm font-semibold text-white px-6 py-3.5 rounded-full border border-brand-blue/40 hover:border-brand-blue hover:bg-brand-blue/10 transition-all duration-300"
            >
              {t.nav.login}
            </a>
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 py-16">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/logo-dark.png" alt="Doble R Records" className="h-8 w-8 object-contain" />
              <span className="font-heading font-bold text-white text-sm leading-tight">
                DOBLE R RECORDS<br /><span className="text-[10px] text-neutral-500 tracking-[0.2em]">DISTRIBUTION</span>
              </span>
            </div>
            <p className="text-neutral-500 text-sm leading-relaxed max-w-xs">{t.footer.tagline}</p>
          </div>

          <div>
            <p className="text-neutral-500 text-xs font-semibold tracking-widest uppercase mb-4">{t.footer.navLabel}</p>
            <ul className="flex flex-col gap-3">
              {NAV_ITEMS.slice(0, 2).map((item) => (
                <li key={item.href}>
                  <a href={`/${item.href}`} data-testid={`footer-link-${item.key}`} className="text-neutral-400 hover:text-white text-sm transition-colors duration-200">
                    {t.nav[item.key]}
                  </a>
                </li>
              ))}
              <li>
                <Link to="/crear-smartlink" data-testid="footer-link-smartlinks" className="text-neutral-400 hover:text-white text-sm transition-colors duration-200">
                  {t.nav.smartlinks}
                </Link>
              </li>
              {NAV_ITEMS.slice(2).map((item) => (
                <li key={item.href}>
                  <a href={`/${item.href}`} data-testid={`footer-link-${item.key}`} className="text-neutral-400 hover:text-white text-sm transition-colors duration-200">
                    {t.nav[item.key]}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-neutral-500 text-xs font-semibold tracking-widest uppercase mb-4">{t.footer.legalLabel}</p>
            <ul className="flex flex-col gap-3">
              {t.footer.legalLinks.map((link, i) => (
                <li key={LEGAL_SLUGS[i]}>
                  <Link to={`/legal/${LEGAL_SLUGS[i]}`} data-testid={`footer-legal-${i}`} className="text-neutral-400 hover:text-brand-blue text-sm transition-colors duration-200">
                    {link}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/formularios-fiscales" data-testid="footer-link-tax-forms" className="text-neutral-400 hover:text-brand-blue text-sm transition-colors duration-200">
                  {t.footer.taxFormsLink}
                </Link>
              </li>
              <li>
                <Link to="/consultar-solicitud" data-testid="footer-link-request-status" className="text-neutral-400 hover:text-brand-blue text-sm transition-colors duration-200">
                  {t.footer.requestStatusLink}
                </Link>
              </li>
              {/* Enlace al generador de portadas oculto a pedido del usuario */}
            </ul>
          </div>

          <div>
            <p className="text-neutral-500 text-xs font-semibold tracking-widest uppercase mb-4">{t.footer.contactLabel}</p>
            <a href="mailto:distribution@doblerrecords.com" data-testid="footer-contact-email" className="flex items-center gap-2 text-neutral-300 hover:text-white text-sm transition-colors duration-200">
              <Mail className="w-4 h-4" />
              distribution@doblerrecords.com
            </a>
            <div className="flex items-center gap-3 mt-5">
              {SOCIAL_LINKS.map(({ key, href, icon: Icon }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${key}`}
                  aria-label={key}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-neutral-400 hover:text-white hover:border-brand-red/50 hover:bg-brand-red/10 transition-all duration-200"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/[0.06]">
          <p data-testid="footer-wordmark" className="font-heading font-black text-white/[0.06] text-[15vw] md:text-[9rem] lg:text-[11rem] leading-[0.8] tracking-tighter select-none -ml-1 whitespace-nowrap overflow-hidden">
            DOBLE R
          </p>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-2 pt-4">
            <p className="text-neutral-600 text-xs">{t.footer.copyright}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
